import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Label } from '@art-of-hospitality-solutions-llc/ui-kit/components/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import {
  toastSuccess,
  toastError,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import service from '@Service';
import type { OperationActionTriggerProps } from '@Constants/operations';
import { hotelReportKeys } from '@Hooks/useHotelReports';
import { useHotelReportRunStore } from './hotelReportRunStore';

// Local calendar date, not UTC — toISOString() rolls over to tomorrow once the
// local clock passes UTC midnight, which would put a future date in the picker.
const toIsoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export default function RunHotelReportTrigger({
  soulId,
  onSuccess,
}: OperationActionTriggerProps) {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const queryClient = useQueryClient();

  // Look-ahead: today through seven days out — the upcoming stays checking in soon.
  const defaultStart = useMemo(() => new Date(), []);
  const defaultEnd = useMemo(() => {
    const d = new Date(defaultStart);
    d.setDate(d.getDate() + 7);
    return d;
  }, [defaultStart]);

  const [startDate, setStartDate] = useState(toIsoDate(defaultStart));
  const [endDate, setEndDate] = useState(toIsoDate(defaultEnd));
  // Email opt-in. Off by default so a manual run just refreshes the feed;
  // tick it to also send each venue's contacts their report.
  const [sendEmail, setSendEmail] = useState(false);

  const handleRun = async () => {
    if (!startDate || !endDate) {
      toastError('Pick a start and end date.');
      return;
    }
    if (endDate < startDate) {
      toastError('End date must be on or after start date.');
      return;
    }
    setRunning(true);
    setOpen(false);
    try {
      const res = await service.runHotelRollup({
        startDate,
        endDate,
        sendEmail,
      });
      if (!res?.data?.started) throw new Error(res?.message);
      // The feed shows the "thinking" thought and polls until the reports land.
      useHotelReportRunStore.getState().begin(soulId);
      queryClient.invalidateQueries({
        queryKey: hotelReportKeys.forSoul(soulId),
      });
      toastSuccess(
        'Hotel rollup started. Reports appear as each property finishes.',
      );
      onSuccess();
    } catch {
      toastError('Failed to start the hotel rollup.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={running}>
          {running ? 'Running…' : 'Run Hotel Rollup'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="flex flex-col space-y-3">
          <div>
            <Heading level="h4">Hotel Rollup Range</Heading>
            <p className="mt-1 text-xs text-zinc-500">
              Hotel stays booked in this range. Each run adds a new digest to the
              feed.
            </p>
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="desk-hotel-start">Start Date</Label>
            <Input
              id="desk-hotel-start"
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="desk-hotel-end">End Date</Label>
            <Input
              id="desk-hotel-end"
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <label className="flex cursor-pointer items-start gap-2 pt-1">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-xs text-zinc-600">
              Send to venue contacts
              <span className="block text-zinc-400">
                Off by default — the run just refreshes the feed. Tick to email each venue's contacts.
              </span>
            </span>
          </label>
          <div className="flex justify-end space-x-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={running}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={handleRun} loading={running}>
              {running ? 'Running…' : 'Run'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
